import type { DateRange } from '@daypicker/react';
import { format, parseISO } from 'date-fns';
import {
  Calendar as CalendarIcon,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Download,
  FileSearch,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { SortableHeader } from '@/components/analytics/components/SortableHeader';
import { Loading, Spinner } from '@/components/shared';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { DateRangeCalendar } from '@/components/ui/DateRangePicker';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  downloadFirmAuditCsv,
  FIRM_AUDIT_PAGE_SIZE,
  type FirmAuditAction,
  type FirmAuditChannel,
  type FirmAuditItem,
  type FirmSortOrder,
  useFirmAudit,
  useFirmMatters,
} from '@/hooks/useFirmAdmin';
import { apiErrorMessageAsync } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { FilterSelect } from './FilterSelect';
import { FirmAdminEmptyState, FirmAdminShell } from './ShellTabs';

const ALL_MATTERS = 'all';

function actionLabel(action: FirmAuditAction): string {
  switch (action) {
    case 'insert':
      return 'Upload';
    case 'view':
      return 'View';
    case 'download':
      return 'Download';
    case 'delete':
      return 'Delete';
    default:
      return action;
  }
}

const ACTION_OPTIONS: Array<{ value: FirmAuditAction; label: string }> = [
  { value: 'insert', label: 'Upload' },
  { value: 'view', label: 'View' },
  { value: 'download', label: 'Download' },
  { value: 'delete', label: 'Delete' },
];

const CHANNEL_LABELS: Record<FirmAuditChannel, string> = {
  ui: 'Web UI',
  case_partner: 'Case Partner',
  superadmin: 'Admin',
};

const CHANNEL_OPTIONS: Array<{ value: FirmAuditChannel; label: string }> = (
  Object.keys(CHANNEL_LABELS) as FirmAuditChannel[]
).map((value) => ({
  value,
  label: CHANNEL_LABELS[value],
}));

function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function endOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

function sameCalendarDay(left?: Date, right?: Date): boolean {
  if (!left || !right) return false;
  return (
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
  );
}

function formatWhen(iso: string): { date: string; time: string } {
  const value = typeof iso === 'string' ? parseISO(iso) : new Date(iso);
  return {
    date: format(value, 'd MMM yyyy'),
    time: format(value, 'HH:mm'),
  };
}

function formatDateRangeLabel(range: DateRange | undefined): string {
  if (!range?.from) return 'All time';
  if (!range.to || sameCalendarDay(range.from, range.to)) {
    return format(range.from, 'd MMM yyyy');
  }
  return `${format(range.from, 'd MMM yyyy')} – ${format(range.to, 'd MMM yyyy')}`;
}

function matterNumber(item: FirmAuditItem): string {
  return item.matter_no || item.case_reference || '—';
}

function ActorCell({ name, email }: { name: string | null; email: string | null }) {
  const displayName = name?.trim() || null;
  const displayEmail = email?.trim() || null;
  if (!displayName && !displayEmail) {
    return <span className="text-sm text-slate-500">—</span>;
  }
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="truncate text-sm text-slate-900" title={displayName ?? displayEmail ?? undefined}>
        {displayName || displayEmail}
      </p>
      {displayName && displayEmail ? (
        <p className="truncate text-xs text-slate-500" title={displayEmail}>
          {displayEmail}
        </p>
      ) : null}
    </div>
  );
}

export function AuditTab() {
  const fileSearchId = useId();
  const personSearchId = useId();
  const [selectedMatterGuid, setSelectedMatterGuid] = useState(ALL_MATTERS);
  const [selectedMatterName, setSelectedMatterName] = useState<string | null>(null);
  const [matterOpen, setMatterOpen] = useState(false);
  const [matterSearch, setMatterSearch] = useState('');
  const [debouncedMatterSearch, setDebouncedMatterSearch] = useState('');
  const [selectedActions, setSelectedActions] = useState<FirmAuditAction[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<FirmAuditChannel[]>([]);
  const [fileQuery, setFileQuery] = useState('');
  const [debouncedFileQuery, setDebouncedFileQuery] = useState('');
  const [personQuery, setPersonQuery] = useState('');
  const [debouncedPersonQuery, setDebouncedPersonQuery] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<FirmSortOrder>('desc');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  const { data: mattersData, isLoading: mattersLoading } = useFirmMatters({
    q: debouncedMatterSearch,
    page: 1,
    pageSize: 20,
    sortField: 'name',
    sortOrder: 'asc',
  });
  const matters = mattersData?.matters ?? [];

  const dateFrom = dateRange?.from ? startOfDay(dateRange.from).toISOString() : undefined;
  const dateTo = dateRange?.to
    ? endOfDay(dateRange.to).toISOString()
    : dateRange?.from
      ? endOfDay(dateRange.from).toISOString()
      : undefined;

  const queryOptions = useMemo(
    () => ({
      matterGuid: selectedMatterGuid === ALL_MATTERS ? undefined : selectedMatterGuid,
      dateFrom,
      dateTo,
      actions: selectedActions,
      channels: selectedChannels,
      file: debouncedFileQuery,
      person: debouncedPersonQuery,
      sortOrder,
      page,
      pageSize: FIRM_AUDIT_PAGE_SIZE,
    }),
    [
      selectedMatterGuid,
      dateFrom,
      dateTo,
      selectedActions,
      selectedChannels,
      debouncedFileQuery,
      debouncedPersonQuery,
      sortOrder,
      page,
    ],
  );

  const { data, isLoading, error } = useFirmAudit(queryOptions);
  const audits = data?.audits ?? [];
  const totalCount = data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / FIRM_AUDIT_PAGE_SIZE));

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedMatterSearch(matterSearch.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [matterSearch]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedFileQuery(fileQuery.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [fileQuery]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedPersonQuery(personQuery.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [personQuery]);

  const hasNarrowingFilters = Boolean(
    selectedMatterGuid !== ALL_MATTERS
    || selectedActions.length > 0
    || selectedChannels.length > 0
    || fileQuery.trim()
    || personQuery.trim()
    || Boolean(dateRange?.from),
  );

  const matterButtonLabel = selectedMatterGuid === ALL_MATTERS
    ? 'All matters'
    : (selectedMatterName ?? 'Selected matter');

  const handleSort = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadFirmAuditCsv({
        matterGuid: queryOptions.matterGuid,
        dateFrom: queryOptions.dateFrom,
        dateTo: queryOptions.dateTo,
        actions: queryOptions.actions,
        channels: queryOptions.channels,
        file: queryOptions.file,
        person: queryOptions.person,
        sortOrder: queryOptions.sortOrder,
      });
    } catch (err) {
      toast.error((await apiErrorMessageAsync(err)) || 'Failed to export file audit CSV');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <FirmAdminShell
      title="Audit"
      description="File uploads, views, downloads, and deletions across matters."
      action={(
        <Button
          type="button"
          variant="outline"
          icon={Download}
          text="Export CSV"
          loading={isExporting}
          loadingText="Exporting…"
          onClick={() => {
            void handleExport();
          }}
        />
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <Popover
          open={matterOpen}
          onOpenChange={(open) => {
            setMatterOpen(open);
            if (!open) setMatterSearch('');
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="md"
              role="combobox"
              aria-expanded={matterOpen}
              aria-label="Filter by matter"
              className={cn(
                'h-10 min-w-60 justify-between',
                selectedMatterGuid !== ALL_MATTERS
                  && 'border-crimson-300 bg-crimson-50/30 hover:bg-crimson-50/50',
              )}
            >
              <span className="min-w-0 truncate">{matterButtonLabel}</span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search matters..."
                value={matterSearch}
                onValueChange={setMatterSearch}
              />
              <CommandList>
                {mattersLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Spinner size="sm" />
                  </div>
                ) : (
                  <CommandEmpty>No matters found.</CommandEmpty>
                )}
                {!mattersLoading ? (
                  <CommandGroup className="max-h-64 overflow-auto">
                    <CommandItem
                      value="all-matters"
                      onSelect={() => {
                        setSelectedMatterGuid(ALL_MATTERS);
                        setSelectedMatterName(null);
                        setPage(1);
                        setMatterOpen(false);
                        setMatterSearch('');
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedMatterGuid === ALL_MATTERS ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      All matters
                    </CommandItem>
                    {matters.map((matter) => (
                      <CommandItem
                        key={matter.matter_guid}
                        value={matter.matter_guid}
                        onSelect={() => {
                          setSelectedMatterGuid(matter.matter_guid);
                          setSelectedMatterName(matter.matter_name);
                          setPage(1);
                          setMatterOpen(false);
                          setMatterSearch('');
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedMatterGuid === matter.matter_guid ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="min-w-0 truncate">{matter.matter_name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : null}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <FilterSelect
          label="Action"
          allLabel="All actions"
          selectedNoun="actions"
          options={ACTION_OPTIONS}
          selected={selectedActions}
          onChange={(next) => {
            setSelectedActions(next as FirmAuditAction[]);
            setPage(1);
          }}
        />

        <FilterSelect
          label="Source"
          allLabel="All sources"
          selectedNoun="sources"
          options={CHANNEL_OPTIONS}
          selected={selectedChannels}
          onChange={(next) => {
            setSelectedChannels(next as FirmAuditChannel[]);
            setPage(1);
          }}
        />

        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex h-10 min-w-50 items-center justify-between gap-2 rounded-md border border-input bg-background px-4 text-sm',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring',
                dateRange?.from && 'border-crimson-300 bg-crimson-50/30 hover:bg-crimson-50/50',
              )}
              aria-label="Filter by date range"
            >
              <span className="flex min-w-0 items-center gap-2">
                <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
                <span className="truncate">{formatDateRangeLabel(dateRange)}</span>
              </span>
              {dateRange?.from ? (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear date range"
                  className="ml-2 text-slate-400 hover:text-slate-600"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDateRange(undefined);
                    setPage(1);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.stopPropagation();
                      setDateRange(undefined);
                      setPage(1);
                    }
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              ) : null}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-fit max-w-[calc(100vw-2rem)] p-0" align="start">
            <DateRangeCalendar
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                setPage(1);
              }}
              showClearSelection
              footerPresets={['today', 'yesterday', 'last7Days', 'last30Days', 'last90Days']}
              onClearSelection={() => {
                setDateRange(undefined);
                setPage(1);
              }}
            />
          </PopoverContent>
        </Popover>

        <div className="relative w-full max-w-xs">
          <label htmlFor={fileSearchId} className="sr-only">Search files</label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            id={fileSearchId}
            value={fileQuery}
            onChange={(event) => setFileQuery(event.target.value)}
            placeholder="Search file or folder"
            className="h-10 pl-9"
          />
        </div>

        <div className="relative w-full max-w-xs">
          <label htmlFor={personSearchId} className="sr-only">Search people</label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            id={personSearchId}
            value={personQuery}
            onChange={(event) => setPersonQuery(event.target.value)}
            placeholder="Search person"
            className="h-10 pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-56 items-center justify-center">
          <Loading />
        </div>
      ) : error ? (
        <FirmAdminEmptyState
          icon={FileSearch}
          title="Could not load audit events"
          description="Try refreshing the page. If this keeps happening, contact support."
        />
      ) : (
        <div>
          <div className="overflow-hidden rounded-md border bg-white">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <SortableHeader
                    label="Date"
                    field="created_at"
                    currentField="created_at"
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    width="170px"
                  />
                  <TableHead style={{ width: '110px' }}>Action</TableHead>
                  <TableHead style={{ width: '180px' }}>Matter</TableHead>
                  <TableHead style={{ width: '220px' }}>File name</TableHead>
                  <TableHead style={{ width: '160px' }}>User</TableHead>
                  <TableHead style={{ width: '110px' }}>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audits.length > 0 ? (
                  audits.map((item) => {
                    const when = formatWhen(
                      typeof item.created_at === 'string'
                        ? item.created_at
                        : new Date(item.created_at).toISOString(),
                    );
                    const action = item.action as FirmAuditAction;
                    const channel = item.channel as FirmAuditChannel;
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="space-y-0.5">
                            <p className="whitespace-nowrap text-sm text-slate-900">{when.date}</p>
                            <p className="text-xs text-slate-500">{when.time}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-900">
                            {actionLabel(action)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="min-w-0 space-y-0.5">
                            <p
                              className="truncate text-sm font-medium text-slate-900"
                              title={item.matter_name ?? undefined}
                            >
                              {item.matter_name || '—'}
                            </p>
                            <p className="truncate text-xs text-slate-500">{matterNumber(item)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="min-w-0 space-y-0.5">
                            <p
                              className="truncate text-sm font-medium text-slate-900"
                              title={item.display_name ?? undefined}
                            >
                              {item.display_name || '—'}
                            </p>
                            <p
                              className="truncate text-xs text-slate-500"
                              title={item.folder_path ?? undefined}
                            >
                              {item.folder_path || '—'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ActorCell name={item.actor_name} email={item.actor_email} />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">
                            {CHANNEL_LABELS[channel] ?? item.channel}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0">
                      <FirmAdminEmptyState
                        icon={FileSearch}
                        title={
                          hasNarrowingFilters
                            ? 'No matching audit events'
                            : 'No file activity yet'
                        }
                        description={
                          hasNarrowingFilters
                            ? 'Try a different matter, action, source, file, person, or date range.'
                            : 'Uploads, views, downloads, and deletions will appear here once recorded.'
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-2 flex items-center justify-between px-2">
            <span className="text-sm text-muted-foreground">
              {totalCount === 0
                ? '0 events'
                : `Showing ${(page - 1) * FIRM_AUDIT_PAGE_SIZE + 1}–${Math.min(page * FIRM_AUDIT_PAGE_SIZE, totalCount)} of ${totalCount} events`}
            </span>
            {totalPages > 1 ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronLeft}
                  aria-label="Previous page"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                />
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronRight}
                  aria-label="Next page"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </FirmAdminShell>
  );
}
